import type {
  BulkUpsertRow,
  EditableDealRow,
  ProductOption,
  RowValidationError,
  SupplierOption,
} from './editable-deal-row.types';

let counter = 0;
// Stable, unique-per-session key for React lists. Avoids Math.random/Date (unavailable in some envs).
function nextClientId(): string {
  counter += 1;
  return `row-${counter}`;
}

export function createEmptyRow(): EditableDealRow {
  return {
    clientId: nextClientId(),
    id: undefined,
    supplier: null,
    product: null,
    cashbackPercent: '0',
    dealPrice: '',
    productExpiryDate: null,
    isNearExpiry: false,
    isActive: true,
  };
}

export function mapDealToRow(deal: any): EditableDealRow {
  const supplier: SupplierOption | null = deal?.supplier
    ? {
        id: deal.supplier.id,
        name: deal.supplier.name ?? `Supplier #${deal.supplier.id}`,
        countryCode: deal.countryCode,
      }
    : null;

  // The category is product-bound: BE returns catalogCategoryId (nullable) on the product.
  // Fall back to a top-level id defensively for older response shapes.
  const product: ProductOption | null = deal?.product
    ? {
        id: deal.product.id,
        nameEn: deal.product.nameEn ?? '',
        brandEn: deal.product.brandEn ?? '',
        defaultUnit: deal.product.defaultUnit ?? '',
        specs: deal.product.specs ?? '',
        origin: deal.product.origin ?? '',
        state: deal.product.state ?? '',
        catalogCategoryId: deal.product.catalogCategoryId ?? deal?.catalogCategoryId ?? null,
        categoryTitle: deal.product.categoryTitle ?? deal?.categoryTitle ?? '',
      }
    : null;

  return {
    clientId: nextClientId(),
    id: deal?.id,
    supplier,
    product,
    cashbackPercent: deal?.cashbackPercent != null ? String(deal.cashbackPercent) : '0',
    dealPrice: deal?.dealPrice != null ? String(deal.dealPrice) : '',
    productExpiryDate: deal?.productExpiryDate ?? null,
    isNearExpiry: deal?.isNearExpiry ?? false,
    isActive: deal?.isActive ?? true,
  };
}

export function appendRow(rows: EditableDealRow[]): EditableDealRow[] {
  return [...rows, createEmptyRow()];
}

export function prependRow(rows: EditableDealRow[]): EditableDealRow[] {
  return [createEmptyRow(), ...rows];
}

export function insertRowAt(rows: EditableDealRow[], index: number, position: 'above' | 'below'): EditableDealRow[] {
  const at = position === 'above' ? index : index + 1;
  const next = [...rows];
  next.splice(at, 0, createEmptyRow());
  return next;
}

export function removeRowAt(rows: EditableDealRow[], index: number): EditableDealRow[] {
  return rows.filter((_, i) => i !== index);
}

// A row counts as "empty" (ignored on save) only when untouched from createEmptyRow().
export function isRowEmpty(row: EditableDealRow): boolean {
  return (
    !row.supplier &&
    !row.product &&
    row.dealPrice === '' &&
    row.productExpiryDate === null &&
    row.isNearExpiry === false &&
    row.isActive === true &&
    row.cashbackPercent === '0' &&
    row.id === undefined
  );
}

// Only rows that will actually be sent (new or changed vs. the pristine snapshot) are
// validated — pre-existing bad data on untouched rows must not block saving the rest.
export function validateRows(rows: EditableDealRow[], original: EditableDealRow[] = []): RowValidationError[] {
  const originalByClientId = new Map(original.map((row) => [row.clientId, row]));
  const errors: RowValidationError[] = [];
  rows.forEach((row, rowIndex) => {
    if (isRowEmpty(row)) return;
    const pristine = originalByClientId.get(row.clientId);
    if (pristine && rowSignature(pristine) === rowSignature(row)) return;
    const messages: string[] = [];
    if (!row.supplier) messages.push('Supplier is required');
    else if (!row.supplier.countryCode) messages.push('Selected supplier has no country code');
    if (!row.product) messages.push('Product is required');
    else if (row.product.catalogCategoryId == null)
      messages.push('Product has no category — set it via the Category column');
    const price = Number(row.dealPrice);
    if (row.dealPrice === '' || !Number.isFinite(price) || price <= 0)
      messages.push('Deal price must be greater than 0');
    if (row.cashbackPercent !== '') {
      const c = Number(row.cashbackPercent);
      if (!Number.isFinite(c) || c < 0 || c > 100) messages.push('Cashback % must be between 0 and 100');
    }
    if (messages.length) errors.push({ clientId: row.clientId, rowIndex, messages });
  });
  return errors;
}

// Stable per-row identity-independent signature for change detection (ignores clientId).
// clientId is destructured in the parameter so it's an ignored arg (^_), not an unused var.
function rowSignature({ clientId: _clientId, ...rest }: EditableDealRow): string {
  return JSON.stringify(rest);
}

// Build the bulk-upsert payload. Only NEW rows (no original counterpart) and CHANGED existing
// rows are emitted — untouched rows are skipped so the BE only bumps `lastModifiedAt` for the
// rows the user actually created or edited. `original` is the pristine snapshot the grid loaded
// with; rows are matched to it by the stable `clientId`.
export function buildBulkPayload(rows: EditableDealRow[], original: EditableDealRow[] = []): BulkUpsertRow[] {
  const originalByClientId = new Map(original.map((row) => [row.clientId, row]));

  return rows
    .filter((row) => !isRowEmpty(row) && row.supplier !== null && row.product !== null)
    .filter((row) => {
      const pristine = originalByClientId.get(row.clientId);
      // No original → newly added row (send it). Original exists → send only if it changed.
      return !pristine || rowSignature(pristine) !== rowSignature(row);
    })
    .map((row) => {
      const payload: BulkUpsertRow = {
        supplierId: row.supplier!.id,
        productId: row.product!.id,
        // Product-bound; validateRows guarantees it's set for every emitted row.
        catalogCategoryId: row.product!.catalogCategoryId as number,
        countryCode: row.supplier!.countryCode,
        cashbackPercent: row.cashbackPercent === '' ? 0 : Number(row.cashbackPercent),
        dealPrice: row.dealPrice === '' ? 0 : Number(row.dealPrice),
        productExpiryDate: row.productExpiryDate,
        isNearExpiry: row.isNearExpiry,
        isActive: row.isActive,
      };
      if (row.id !== undefined) payload.id = row.id;
      return payload;
    });
}

// Compares ignoring clientId (which is presentational only).
// Note: order-sensitive — fine while rows are only appended/inserted/removed (no drag-reorder).
export function hasUnsavedChanges(current: EditableDealRow[], original: EditableDealRow[]): boolean {
  const strip = (rows: EditableDealRow[]) => JSON.stringify(rows.map(({ clientId: _clientId, ...rest }) => rest));
  return strip(current) !== strip(original);
}
